<template>
	<div class="relative min-h-[100dvh] w-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
		<!-- 🌗 Dark/Light Mode Toggle (Mobile - above logo) -->
		<button
		@click="themeStore.toggleTheme()"
		class="md:hidden absolute top-4 right-4 p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition z-10"
		aria-label="Toggle dark mode"
		>
			<SunIcon v-if="isDarkMode"/>
			<MoonIcon v-else/>
		</button>

		<div class="w-full min-h-[100dvh] flex flex-col md:flex-row bg-white dark:bg-gray-800 overflow-hidden">
			<div
				class="hidden md:flex w-1/2 bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 items-center justify-center p-8"
			>
				<div class="text-center text-white space-y-6">
					<img src='images/main-logo-dark.png' alt="App Logo" class="mx-auto h-24 w-[18rem] object-contain drop-shadow-md" />
					<h2 class="text-3xl font-bold">{{ appTitle }}</h2>
					<div class="h-8 flex items-center justify-center overflow-hidden">
						<Transition name="rotate" mode="out-in">
							<span class="block text-lg font-medium" :key="currentTagline">{{ taglines[currentTagline] }}</span>
						</Transition>
					</div>
				</div>
			</div>

			<!-- Right Form Section -->
			<div class="flex flex-col justify-center items-center w-full md:w-1/2 p-4 sm:p-8 lg:p-12 relative flex-1 md:flex-none overflow-hidden">
				<div class="flex md:hidden flex-col items-center justify-center pt-4 pb-8 px-4 shrink-0">
					<img :src="isDarkMode ? 'images/main-logo-dark.png' : 'images/main-logo.png'" alt="App Logo" class="h-16 w-auto max-w-[18rem] object-contain drop-shadow-sm" />
				</div>
				<button
				@click="themeStore.toggleTheme()"
				class="hidden md:block absolute top-6 right-6 p-2 rounded-full text-slate-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
				aria-label="Toggle dark mode"
				>
					<!-- Sun Icon -->
					<SunIcon v-if="isDarkMode"/>
					<!-- Moon Icon -->
					<MoonIcon v-else/>

				</button>
				<!-- Form Container with Border -->
				<div class="w-full max-w-md space-y-4 md:space-y-8 relative">
					<div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-gray-800/70 rounded-2xl">
						<svg class="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
					</div>
					<div class="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700">
						<form class="space-y-4 md:space-y-6" @submit.prevent="handleLogin">
							<!-- Heading -->
							<label class="block text-lg font-bold text-slate-800 dark:text-slate-200 mb-6 justify-center text-center">Sign in to your account</label>

							<!-- Username / Email (auto-filled when role changes) -->
							<div>
								<label
									for="email"
									class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
								>
								Email address or Username
								</label>
								<input
									id="text"
									type="text"
									v-model="email"
									class="w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-300 outline-none"
									placeholder="you@example.com"
								/>
							</div>

							<!-- Password (auto-filled when role changes) -->
							<div>
								<label
								for="password"
								class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
								>
								Password
								</label>
								<div class="relative">
									<input
									id="password"
									:type="showPassword ? 'text' : 'password'"
									v-model="password"
									class="w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-300 outline-none pr-10"
									placeholder="********"
									/>
									<button
										type="button"
										@click="showPassword = !showPassword"
										class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
									>
										<component :is="showPassword ? EyeOff : Eye" class="h-5 w-5" />
									</button>
								</div>
							</div>

							<!-- Remember me + Forgot password -->
							<div class="flex items-center justify-between text-sm">
								<label class="flex items-center text-slate-600 dark:text-slate-400">
								<input
									id="remember-me"
									name="remember-me"
									type="checkbox"
									v-model="rememberMe"
									class="rounded border-gray-300 text-orange-400 focus:ring-orange-300 mr-2"
								/>
								Remember me
								</label>
								<a
								href="/portal/forgot-password"
								class="text-primary hover:text-primary-hover dark:text-secondary-dark-text dark:hover:text-secondary-dark-hover font-medium hover:underline"
								>Forgot your password?</a>
							</div>

							<button
								type="submit"
								:disabled="loading || !email || !password"
								class="w-full py-2 md:py-3 px-4 bg-primary hover:bg-primary-hover dark:bg-primary-dark dark:hover:bg-primary-dark-hover text-white font-bold rounded-lg shadow-lg transform transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
							>
								{{ loading ? 'Signing in...' : 'Sign in' }}
							</button>
						</form>

						<!-- Register redirect -->
						<p class="text-center text-sm text-slate-600 dark:text-slate-400 mt-6">
						Don't have an account?
						<a
							href="/portal/register"
							class="text-primary hover:text-primary-hover dark:text-secondary-dark-text dark:hover:text-secondary-dark-hover font-bold hover:underline"
						>
							Register
						</a>
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useThemeStore } from '@/stores/theme'
// import { useAuthStore } from '@/stores/auth'
import { useLogin } from '@/composables/auth/useLogin'
// import { useRouter } from 'vue-router'
// import { getLastAccessibleRoute } from '@/router'
import SunIcon from '@/components/icons/SunIcon.vue'
import MoonIcon from '@/components/icons/MoonIcon.vue'
import { Eye, EyeOff } from 'lucide-vue-next'
import { useToast } from 'vue-toastification'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'PLANET Home Healthcare')

const taglines = [
	'Compassionate Care',
	'Your Independence',
	'Quality Home Health',
	'Caring Professionals',
]
const currentTagline = ref(0)


const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)
// const authStore = useAuthStore()
// const router = useRouter()

const email = ref('')
const password = ref('')
const rememberMe = ref(false)
const showPassword = ref(false)

const toast = useToast()
const toastTimeout = 5000

const { loading, error, handleLogin: login } = useLogin({ email, password, rememberMe })

const handleLogin = async () => {
	await login()

	// console.log(error.value)

	if (error.value) {
		toast.error(error.value, { timeout: toastTimeout })
	}
}

onMounted(() => {
	setInterval(() => {
		currentTagline.value = (currentTagline.value + 1) % taglines.length
	}, 3500)
})
</script>

<style scoped>
.rotate-enter-active {
	animation: rotateIn 0.4s ease-out;
}
.rotate-leave-active {
	animation: rotateOut 0.3s ease-in;
}
@keyframes rotateIn {
	from { opacity: 0; transform: translateY(12px) rotateX(-10deg); }
	to { opacity: 1; transform: translateY(0) rotateX(0); }
}
@keyframes rotateOut {
	from { opacity: 1; transform: translateY(0) rotateX(0); }
	to { opacity: 0; transform: translateY(-12px) rotateX(10deg); }
}
</style>

